import React, { useEffect, useState } from 'react';
import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Snackbar,
  Alert,
  Autocomplete, // Added Autocomplete import
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { toast } from 'react-toastify';
import BeatLoader from 'react-spinners/BeatLoader';

export default function ItemDialog({ dialogOpen, setDialogOpen, updateID }) {
  const [itemData, setItemData] = useState({
    supplierName: '',
    grndate: '',
    totalAmount: '',
    paidAmount: '',
    pendingAmount: '',
    itemName: '',
    qty: '',
    baseunitprice: '',
    paymentMethod: 'Cash',
    expirationDate: '',
  });
  const [supplierList, setSupplierList] = useState([]);
  const [itemList, setItemList] = useState([]);

  const [selectedItem, setSelectedItem] = useState(null); // Changed initial state to null
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSuppliers();
    fetchItems();

    // Set default GRN date to today if not updating
    if (!updateID) {
      const today = new Date().toISOString().split('T')[0];
      setItemData((prev) => ({ ...prev, grndate: today }));
    }
  }, []);

  useEffect(() => {
    if (updateID) {
      fetchGRNDetails(updateID);
    }
  }, [updateID]);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(
        'https://serveremilia.mylksystems.com/Suppliers/getallactivesuppliers'
      );
      setSupplierList(res.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Failed to fetch suppliers.');
    }
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get(
        'https://serveremilia.mylksystems.com/Item/getItemDetails'
      );
      setItemList(res.data);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to fetch items.');
    }
  };

  const fetchGRNDetails = async (id) => {
    try {
      const data = {
        grnKey: id,
      };
      // Fetch GRN details here if needed
    } catch (error) {
      console.error('Error fetching GRN details:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update state and calculate values
    setItemData((prev) => {
      let updated = {
        ...prev,
        [name]: value,
      };

      // Parse numerical values
      const qty = parseFloat(updated.qty) || 0;
      const totalAmount = parseFloat(updated.totalAmount) || 0;
      const paidAmount = parseFloat(updated.paidAmount) || 0;

      // Calculate baseunitprice as totalAmount / qty
      if (name === 'qty' || name === 'totalAmount') {
        if (qty > 0) {
          updated.baseunitprice = (totalAmount / qty).toFixed(2);
        } else {
          updated.baseunitprice = '';
        }
      }

      // Calculate pendingAmount as totalAmount - paidAmount
      if (name === 'totalAmount' || name === 'paidAmount') {
        updated.pendingAmount = (totalAmount - paidAmount).toFixed(2);
      }

      return updated;
    });
  };

  const handleClose = () => {
    setDialogOpen(false);
    clearForm();
  };

  const clearForm = () => {
    const today = new Date().toISOString().split('T')[0];
    setItemData({
      supplierName: '',
      grndate: today,
      totalAmount: '',
      paidAmount: '',
      pendingAmount: '',
      itemName: '',
      qty: '',
      baseunitprice: '',
      paymentMethod: 'Cash',
      productExpiry: 'No',
      expirationDate: '',
    });
    setSelectedItem(null); // Clear selected item
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (
      !itemData.supplierName ||
      !itemData.grndate ||
      !selectedItem ||
      !itemData.qty ||
      !itemData.totalAmount
    ) {
      toast.error('Please fill in all required fields.');
      return;
    }


    // Prepare data to send
    const dataToSend = {
      itemName: selectedItem.ItemName,
      itemId: selectedItem.ItemID,
      supplierName: itemData.supplierName,
      qty: parseInt(itemData.qty),
      totalAmount: parseFloat(itemData.totalAmount),
      paidAmount: parseFloat(itemData.paidAmount) || 0,
      pendingAmount: parseFloat(itemData.pendingAmount) || 0,
      baseunitprice: parseFloat(itemData.baseunitprice) || 0,
      expirationDate:
        itemData.productExpiry === 'Yes'
          ? itemData.expirationDate
          : '2100-12-31',
      grndate: itemData.grndate,
      paymentMethod: itemData.paymentMethod,
    };

    const stockData = {
      itemId: selectedItem.ItemID,
      itemName: selectedItem.ItemName,
      itemCategoryId: selectedItem.ItemCategoryID,
      stockIndate: itemData.grndate,
      stockExpireDate:
        itemData.productExpiry === 'Yes'
          ? itemData.expirationDate
          : '2100-12-31',
      stock: parseInt(itemData.qty),
    };

    //console.log(stockData)
    //console.log(dataToSend)

    if (updateID) {
      // Update existing GRN
      axios
        .post(
          'https://serveremilia.mylksystems.com/grn/updategrn',
          dataToSend
        )
        .then((res) => {
          toast.success('GRN successfully updated!');
          handleClose();
        })
        .catch((err) => {
          console.error('Error updating GRN:', err);
          toast.error('Failed to update GRN.');
        });
    } else {
      // Create new GRN
      setLoading(true);
      axios
        .post(
          'https://serveremilia.mylksystems.com/grn/CreateGrn',
          dataToSend
        )
        .then((res) => {
          axios
            .post(
              'https://serveremilia.mylksystems.com/stock/CreateShop1Stock',
              stockData
            )
            .then((res) => {
              setLoading(false);
              toast.success('GRN successfully created!');
              handleClose();
            })
            .catch((e) => {
              toast.error('Failed to update stock.');
              setLoading(false);
            });
        })
        .catch((err) => {
          console.error('Error submitting form:', err);
          toast.error('Failed to create GRN.');
          setLoading(false);
        });
    }
  };

  return (
    <Dialog
      open={dialogOpen}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
    >
      <DialogTitle>
        {updateID ? 'UPDATE GRN' : 'CREATE GRN'}
        <IconButton
          onClick={handleClose}
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#000',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Container>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Supplier Dropdown */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  name="supplierName"
                  label="Supplier "
                  value={itemData.supplierName}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="">Select Supplier</MenuItem>
                  {supplierList.map((supplier) => (
                    <MenuItem
                      key={supplier.SupplierID}
                      value={supplier.SupplierName}
                    >
                      {supplier.SupplierName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {/* GRN Date */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="grndate"
                  label="GRN Date *"
                  type="date"
                  value={itemData.grndate}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>
              {/* Item Dropdown (Searchable) */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={itemList}
                    getOptionLabel={(option) =>
                      `${option.ItemName} (${option.ItemID})`
                    }
                    onChange={(event, newValue) => {
                      setSelectedItem(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item "
                        required
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                    filterOptions={(options, { inputValue }) =>
                      options.filter(
                        (option) =>
                          option.ItemName.toLowerCase().includes(
                            inputValue.toLowerCase()
                          ) ||
                          option.ItemID.toString().includes(inputValue)
                      )
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <span style={{ fontWeight: 'bold' }}>
                          {option.ItemName}
                        </span>{' '}
                        (ID: {option.ItemID})
                      </li>
                    )}
                    value={selectedItem}
                  />
                </FormControl>
              </Grid>
              {/* Quantity */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="qty"
                  label="Quantity *"
                  type="number"
                  value={itemData.qty}
                  onChange={handleChange}
                  required
                />
              </Grid>
              {/* Total Amount */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="totalAmount"
                  label="Total Amount *"
                  type="number"
                  value={itemData.totalAmount}
                  onChange={handleChange}
                  required
                />
              </Grid>
              {/* Base Unit Price (Auto-calculated) */}
              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="baseunitprice"
                  label="Base Unit Price"
                  type="number"
                  value={itemData.baseunitprice}
                  disabled
                />
              </Grid> */}
              {/* Paid Amount */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="paidAmount"
                  label="Paid Amount"
                  type="number"
                  value={itemData.paidAmount}
                  onChange={handleChange}
                />
              </Grid>
              {/* Pending Amount (Auto-calculated) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="pendingAmount"
                  label="Pending Amount"
                  type="number"
                  value={itemData.pendingAmount}
                  disabled
                />
              </Grid>
              {/* Payment Method */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  name="paymentMethod"
                  label="Payment Method "
                  value={itemData.paymentMethod}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="Cheque">Cheque</MenuItem>
                  <MenuItem value="Online">Online transfer</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                </TextField>
              </Grid>
              {/* Submit Button */}
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <Button variant="outlined" type="submit" fullWidth>
                  {loading ? (
                    <>
                      <BeatLoader color="#000000" />
                    </>
                  ) : (
                    <>{updateID ? 'Update' : 'Submit'}</>
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Container>
      </DialogContent>
    </Dialog>
  );
}
